"use client"

import { useSearchParams, type ReadonlyURLSearchParams } from "next/navigation"

const EMPTY_SEARCH_PARAMS = new URLSearchParams() as ReadonlyURLSearchParams

/**
 * `useSearchParams()` that does not bail its Suspense subtree out of static
 * prerendering. Next's hook throws during static generation, so every client
 * component that reads it (and all the copy around it) is missing from the
 * server HTML crawlers receive.
 *
 * On the server this returns empty params, so the prerendered HTML is the
 * no-query state. On the client it is the real Next hook, updating on every
 * navigation. The server and client each take one fixed branch, so hook order
 * is stable within each environment.
 *
 * Any component whose first render depends on the params must sit inside a
 * `<SearchParamsHydrationGate>` so a query-string landing client-renders
 * instead of hydrating against the no-query HTML.
 */
export function usePrerenderSafeSearchParams(): ReadonlyURLSearchParams {
  if (typeof window === "undefined") return EMPTY_SEARCH_PARAMS
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useSearchParams()
}
